// src/Admin.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import Login from './admincomponents/LoginAdmin';
import Dashboard from './admincomponents/Dashboard'; // 我们将主要内容逻辑放在 Dashboard 中
import SystemDashboard from './admincomponents/SystemDashboard';
import AdminQuestionBank from './admincomponents/AdminQuestionBank';
import ManageAnnouncements from './admincomponents/ManageAnnouncements';
import WebmasterFiles from './admincomponents/WebmasterFiles';
import LoadingSpinner from '../opencomponents/LoadingSpinner';
import PageShell from '../components/PageShell';
import { Muted } from '../components/ui';
import { Panel, PanelHeader, PanelTitle } from './admincomponents/adminStyles';
import { FaTachometerAlt, FaUsers, FaCog, FaQuestionCircle, FaBullhorn} from 'react-icons/fa'; // 引入图标
import { FiActivity } from "react-icons/fi";
import { color, font } from '../theme/tokens';

// --- 样式定义 ---
const AdminLayout = styled.div`
    display: grid;
    grid-template-columns: 200px minmax(0, 1fr);
    gap: 32px;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 24px;
    }
`;

const SidebarNav = styled.nav`
    position: sticky;
    top: 88px;

    ul {
        list-style: none;
        margin: 0;
        padding: 0;
        border-top: 1px solid ${color.line};
    }

    @media (max-width: 900px) {
        position: static;
        ul {
            display: flex;
            flex-wrap: wrap;
            border-top: none;
            border-bottom: 1px solid ${color.line};
        }
    }
`;

const NavButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 14px 16px;
    border: none;
    border-bottom: 1px solid ${color.line};
    border-radius: 0;
    background: ${({ $active }) => ($active ? color.surface : 'transparent')};
    color: ${({ $active }) => ($active ? color.text : color.text2)};
    font-family: ${font.display};
    font-size: 15px;
    letter-spacing: 0.18em;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;

    svg {
        flex-shrink: 0;
        font-size: 14px;
        color: ${({ $active }) => ($active ? color.bloodHi : color.text3)};
    }

    &::before {
        content: '';
        position: absolute;
        left: 0; top: 0; bottom: 0;
        width: 2px;
        background: ${color.blood};
        opacity: ${({ $active }) => ($active ? 1 : 0)};
    }

    &:hover { background: ${color.surface2}; color: ${color.text}; }

    @media (max-width: 900px) {
        width: auto;
        border-bottom: none;
        padding: 10px 14px;
        font-size: 14px;
        letter-spacing: 0.1em;
        &::before { left: 0; right: 0; top: auto; bottom: 0; width: auto; height: 2px; }
    }
`;

const ContentArea = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const LoginArea = styled.div`
    display: flex;
    justify-content: center;
    padding: 24px 0;
`;


// --- 左侧栏组件 ---
const Sidebar = ({ activeView, setActiveView }) => {
    const navItems = [
        { id: 'dashboard', label: '仪表盘', icon: <FaTachometerAlt aria-hidden="true" /> },
        { id: 'userManagement', label: '用户管理', icon: <FaUsers aria-hidden="true" /> },
        { id: 'questionBank', label: '题库管理', icon: <FaQuestionCircle aria-hidden="true" /> },
        { id: 'announcements', label: '公告管理', icon: <FaBullhorn aria-hidden="true" /> },
        { id: 'siteweblink', label:'测试功能', icon: <FiActivity aria-hidden="true" /> },
        { id: 'siteSettings', label: '网站设置', icon: <FaCog aria-hidden="true" /> },
    ];

    return (
        <SidebarNav aria-label="管理分区">
            <ul>
                {navItems.map(item => (
                    <li key={item.id}>
                        <NavButton
                            type="button"
                            $active={activeView === item.id}
                            aria-current={activeView === item.id ? 'page' : undefined}
                            onClick={() => setActiveView(item.id)}
                        >
                            {item.icon}
                            {item.label}
                        </NavButton>
                    </li>
                ))}
            </ul>
        </SidebarNav>
    );
};


function Admin() {
    const { user, onLoginSuccess, onLogout, loading } = useAuth();
    const [activeView, setActiveView] = useState('userManagement'); // 默认视图为用户管理

    const isAuthorized = user && (user.role === 'admin' || user.role === 'site_owner');

    if (loading) {
        return <LoadingSpinner />;
    }

    // 未授权（登录）时的视图
    if (!isAuthorized) {
        return (
            <PageShell title="管理" en="ADMIN" wide>
                <LoginArea>
                    <Login onLogin={onLoginSuccess} />
                </LoginArea>
            </PageShell>
        );
    }

    // 已授权（登录后）的视图
    return (
        <PageShell title="管理" en="ADMIN" wide>
            <AdminLayout>
                <Sidebar activeView={activeView} setActiveView={setActiveView} />
                <ContentArea>
                    {/* 根据 activeView 渲染不同的组件 */}
                    {activeView === 'userManagement' && <Dashboard user={user} onLogout={onLogout} />}
                    {activeView === 'dashboard' && <SystemDashboard />}
                    {activeView === 'questionBank' && <AdminQuestionBank/>}
                    {activeView === 'announcements' && <ManageAnnouncements/>}
                    {activeView === 'siteweblink' && <WebmasterFiles/>}
                    {activeView === 'siteSettings' && (
                        <Panel>
                            <PanelHeader><PanelTitle>网站设置</PanelTitle></PanelHeader>
                            <Muted>此页面待开发…</Muted>
                        </Panel>
                    )}
                </ContentArea>
            </AdminLayout>
        </PageShell>
    );
}

export default Admin;
